/* AMPD comparison section.

   Handles:
   - keeping the two horizontal scrollers (headers + specs) column-aligned
   - the Model size / Frame style pill toggles, incl. the sliding thumb
   - colour swatches switching each product's image
   - measuring the product image so the sticky header pins with only the
     title/price/button strip visible (see ampd-compare.css)
*/
(function () {
  'use strict';

  /* ---------- horizontal scroll sync ---------- */
  function initSync(cmp) {
    var scrollers = cmp.querySelectorAll('[data-ampd-sync]');
    if (scrollers.length < 2) return;
    var lock = false;
    scrollers.forEach(function (s) {
      s.addEventListener('scroll', function () {
        if (lock) return;
        lock = true;
        scrollers.forEach(function (o) { if (o !== s) o.scrollLeft = s.scrollLeft; });
        requestAnimationFrame(function () { lock = false; });
      }, { passive: true });
    });
  }

  /* ---------- sliding pill thumb ---------- */
  function moveThumb(toggle) {
    var thumb = toggle.querySelector('.ampd-compare__toggle-thumb');
    var active = toggle.querySelector('.ampd-compare__toggle-btn.is-active');
    if (!thumb || !active) return;
    thumb.style.width = active.offsetWidth + 'px';
    thumb.style.transform = 'translateX(' + active.offsetLeft + 'px)';
    toggle.classList.add('is-ready');
  }

  function setActive(toggle, btn) {
    toggle.querySelectorAll('.ampd-compare__toggle-btn').forEach(function (b) {
      var on = b === btn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    moveThumb(toggle);
  }

  /* ---------- panel switching ----------
     Panels are keyed size + series ("a5", "b4", ...). The two toggles each own
     one half of the key; combining them here means adding a series never
     multiplied the toggle logic. */
  function panelKey(cmp) {
    return (cmp.dataset.ampdSize || 'a') + (cmp.dataset.ampdSeries || '5');
  }

  function productMode(cmp) {
    return cmp.getAttribute('data-frame-mode') === 'product';
  }

  function applyPanels(cmp) {
    var key = panelKey(cmp);
    // A size that doesn't exist for this series (e.g. no minis in Series 4)
    // falls back to the first size that does, rather than showing an empty grid.
    if (!cmp.querySelector('[data-ampd-set-panel="' + key + '"]')) {
      var sizes = ['a', 'b', 'c'];
      for (var i = 0; i < sizes.length; i++) {
        if (cmp.querySelector('[data-ampd-set-panel="' + sizes[i] + (cmp.dataset.ampdSeries || '5') + '"]')) {
          cmp.dataset.ampdSize = sizes[i];
          break;
        }
      }
      key = panelKey(cmp);
      var sizeBtn = cmp.querySelector('[data-ampd-set="' + cmp.dataset.ampdSize + '"]');
      if (sizeBtn) setActive(sizeBtn.closest('.ampd-compare__toggle'), sizeBtn);
    }
    cmp.querySelectorAll('[data-ampd-set-panel]').forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-ampd-set-panel') !== key;
    });
    // Column count drives the grid + divider segments, and differs per panel.
    var cols = cmp.getAttribute('data-cols-' + key);
    if (cols && parseInt(cols, 10) > 0) {
      cmp.style.setProperty('--ampd-compare-cols', cols);
      rebuildDivider(cmp, parseInt(cols, 10));
    }
    // In product frame mode the frame filter carries over to the new panel,
    // and it owns the real column count (it may be hiding products).
    if (productMode(cmp)) applyFrameFilter(cmp);
    else applyVariantFrameFilter(cmp);
    // Newly-shown panels were display:none, so images had no measurable height.
    requestAnimationFrame(function () { measure(cmp); });
  }

  /* Product frame mode: step-through models are standalone products, so the
     frame toggle hides the other frame's products instead of switching
     variants. Every affected cell (header + each spec row) carries
     data-ampd-frame-cat, so one pass keeps them all in sync; the column count
     then has to be recomputed from what's actually visible. */
  function applyFrameFilter(cmp) {
    var frame = cmp.dataset.ampdFrame || 'a';
    cmp.querySelectorAll('[data-ampd-frame-cat]').forEach(function (cell) {
      cell.hidden = cell.getAttribute('data-ampd-frame-cat') !== frame;
    });
    var key = panelKey(cmp);
    var head = cmp.querySelector('.ampd-compare__scroll--head [data-ampd-set-panel="' + key + '"]');
    if (!head) return;
    var visible = 0;
    for (var i = 0; i < head.children.length; i++) {
      if (!head.children[i].hidden) visible++;
    }
    if (!visible) return;
    cmp.style.setProperty('--ampd-compare-cols', visible);
    // Panels carry their own inline cols var, which beats the one above.
    cmp.querySelectorAll('[data-ampd-set-panel="' + key + '"]').forEach(function (panel) {
      panel.style.setProperty('--ampd-compare-cols', visible);
    });
    rebuildDivider(cmp, visible);
  }

  /* Variant frame mode: most bikes carry both frames as variants of one
     product, but some models simply don't come in the second frame. Those were
     shown anyway with a "no step through option" note, which reads as though
     the bike is part of the answer when it isn't. With the merchant setting on
     we drop those columns while the second frame is selected instead.

     Header and spec cells for the same bike share a data-ampd-col index, so
     hiding a column is one pass over the active panel. Mirrors
     applyFrameFilter, which does the same job when frames are whole products. */
  function applyVariantFrameFilter(cmp) {
    if (productMode(cmp) || !cmp.hasAttribute('data-hide-missing-frame')) return;
    var frame = cmp.dataset.ampdFrame || 'a';
    var key = panelKey(cmp);
    var head = cmp.querySelector('.ampd-compare__scroll--head [data-ampd-set-panel="' + key + '"]');
    if (!head) return;

    var drop = {};
    var visible = 0;
    Array.prototype.forEach.call(head.children, function (cell) {
      // data-ampd-frames lists the frames this bike is genuinely sold in
      // ("ab", "a", "b"), measured from its variants. Falling back to
      // data-ampd-no-frame keeps older markup working if a page is cached.
      var avail = cell.getAttribute('data-ampd-frames');
      var hide = avail
        ? avail.indexOf(frame) === -1
        : (frame === 'b' && cell.hasAttribute('data-ampd-no-frame'));
      if (hide) drop[cell.getAttribute('data-ampd-col')] = true;
      else visible++;
      cell.hidden = hide;
    });

    // Never leave an empty grid: if nothing survives, show them all and let
    // syncFrameAvailability grey the pill out instead.
    if (!visible) {
      Array.prototype.forEach.call(head.children, function (cell) { cell.hidden = false; });
      return;
    }

    cmp.querySelectorAll('[data-ampd-set-panel="' + key + '"] .ampd-compare__cell--spec').forEach(function (cell) {
      cell.hidden = !!drop[cell.getAttribute('data-ampd-col')];
    });
    cmp.style.setProperty('--ampd-compare-cols', visible);
    cmp.querySelectorAll('[data-ampd-set-panel="' + key + '"]').forEach(function (panel) {
      panel.style.setProperty('--ampd-compare-cols', visible);
    });
    rebuildDivider(cmp, visible);
  }

  /* Series 4 ships without Step Through frames, so while it's selected the
     Step Through pill is greyed out and the selection snaps back to Step Over.
     Driven by data-series-b-no-frame-b so it stays a merchant setting rather
     than hardcoded knowledge. */
  function syncFrameAvailability(cmp) {
    var btnA = cmp.querySelector('.ampd-compare__toggle-btn[data-ampd-frame="a"]');
    var btnB = cmp.querySelector('.ampd-compare__toggle-btn[data-ampd-frame="b"]');
    if (!btnB) return;

    var offA = false;
    var offB = false;

    if (productMode(cmp)) {
      // Grey out a frame pill when the active panel simply has no products of
      // that frame (e.g. a range with no step-through model).
      var head = cmp.querySelector('.ampd-compare__scroll--head [data-ampd-set-panel="' + panelKey(cmp) + '"]');
      if (head) {
        var counts = { a: 0, b: 0 };
        head.querySelectorAll('[data-ampd-frame-cat]').forEach(function (cell) {
          counts[cell.getAttribute('data-ampd-frame-cat')]++;
        });
        offA = counts.a === 0;
        offB = counts.b === 0;
      }
    } else {
      offB = cmp.dataset.ampdSeries === '4' && cmp.hasAttribute('data-series-b-no-frame-b');
      // Hiding is on and not one bike in this panel offers the second frame,
      // so the pill would lead to an empty comparison.
      if (!offB && cmp.hasAttribute('data-hide-missing-frame')) {
        var vhead = cmp.querySelector('.ampd-compare__scroll--head [data-ampd-set-panel="' + panelKey(cmp) + '"]');
        if (vhead && vhead.children.length) {
          var withFrame = 0;
          Array.prototype.forEach.call(vhead.children, function (cell) {
            var av = cell.getAttribute('data-ampd-frames');
            if (av ? av.indexOf('b') !== -1 : !cell.hasAttribute('data-ampd-no-frame')) withFrame++;
          });
          offB = withFrame === 0;
        }
      }
    }

    if (btnA) {
      btnA.classList.toggle('is-disabled', offA);
      btnA.setAttribute('aria-disabled', offA ? 'true' : 'false');
    }
    btnB.classList.toggle('is-disabled', offB);
    btnB.setAttribute('aria-disabled', offB ? 'true' : 'false');

    // If the selected frame just became unavailable, snap to the other one.
    var current = cmp.dataset.ampdFrame || 'a';
    var snapTo = null;
    if (current === 'b' && offB) snapTo = btnA;
    if (current === 'a' && offA) snapTo = btnB;
    if (snapTo && !snapTo.classList.contains('is-disabled')) {
      setActive(snapTo.closest('.ampd-compare__toggle'), snapTo);
      showFrame(cmp, snapTo.getAttribute('data-ampd-frame'));
    }
  }

  /* Grey out size pills when the current series has no products for them. */
  function syncSizeAvailability(cmp) {
    cmp.querySelectorAll('.ampd-compare__toggle-btn[data-ampd-set]').forEach(function (btn) {
      var key = btn.getAttribute('data-ampd-set') + (cmp.dataset.ampdSeries || '5');
      var off = !cmp.querySelector('[data-ampd-set-panel="' + key + '"]');
      btn.classList.toggle('is-disabled', off);
      btn.setAttribute('aria-disabled', off ? 'true' : 'false');
    });
  }

  function rebuildDivider(cmp, cols) {
    var divider = cmp.querySelector('[data-ampd-divider]');
    if (!divider || !cols) return;
    var have = divider.children.length;
    while (divider.children.length > cols) divider.removeChild(divider.lastChild);
    for (var i = have; i < cols; i++) {
      var seg = document.createElement('span');
      seg.className = 'ampd-compare__seg';
      divider.appendChild(seg);
    }
  }

  function showFrame(cmp, key) {
    cmp.dataset.ampdFrame = key;
    if (productMode(cmp)) {
      // Frame toggle filters whole products rather than switching variants.
      applyFrameFilter(cmp);
      requestAnimationFrame(function () { measure(cmp); });
      return;
    }
    cmp.querySelectorAll('[data-ampd-frame-panel]').forEach(function (panel) {
      var pk = panel.getAttribute('data-ampd-frame-panel');
      // "any" panels belong to products with no Frame option — always visible.
      panel.hidden = !(pk === key || pk === 'any');
    });
    // Show the "not available in ..." note only on the non-default frame.
    cmp.querySelectorAll('[data-ampd-framenote]').forEach(function (note) {
      note.hidden = key !== 'b';
    });
    applyVariantFrameFilter(cmp);
    cmp.querySelectorAll('.ampd-compare__cell--head').forEach(syncCellLinks);
    requestAnimationFrame(function () { measure(cmp); });
  }

  function initToggles(cmp) {
    // Liquid opens on the first size that has products, which isn't always A.
    cmp.dataset.ampdSize = cmp.getAttribute('data-default-size') || 'a';
    cmp.dataset.ampdSeries = '5';
    cmp.dataset.ampdFrame = 'a';
    cmp.querySelectorAll('.ampd-compare__toggle').forEach(function (toggle) {
      moveThumb(toggle);
      toggle.addEventListener('click', function (e) {
        var btn = e.target.closest('.ampd-compare__toggle-btn');
        if (!btn || btn.classList.contains('is-active') || btn.classList.contains('is-disabled')) return;
        setActive(toggle, btn);
        if (btn.hasAttribute('data-ampd-set')) {
          cmp.dataset.ampdSize = btn.getAttribute('data-ampd-set');
          applyPanels(cmp);
          // The new panel may lack the selected frame's products entirely.
          syncFrameAvailability(cmp);
        }
        if (btn.hasAttribute('data-ampd-series')) {
          cmp.dataset.ampdSeries = btn.getAttribute('data-ampd-series');
          syncSizeAvailability(cmp);
          applyPanels(cmp);
          syncFrameAvailability(cmp);
        }
        if (btn.hasAttribute('data-ampd-frame')) showFrame(cmp, btn.getAttribute('data-ampd-frame'));
      });
    });
    if (productMode(cmp)) applyFrameFilter(cmp);
    else applyVariantFrameFilter(cmp);
    syncFrameAvailability(cmp);
    syncSizeAvailability(cmp);
    applyDeepLink(cmp);
  }

  /* ---------- deep links ----------
     Ads need to land on a pre-set view: the same collection page, but opened
     on the 16" models, or on Step Through, or on Series 4. So the toggles read
     ?size= / ?frame= / ?series= on load and set themselves accordingly.

     Values are matched against the pill's own data attribute first (a/b/c,
     4/5) and then against its visible label, so "?size=16" works without the
     ad needing to know the internal letter - and keeps working if the pills
     are relabelled, as long as the number in the label stays. */
  function slug(v) {
    return String(v == null ? '' : v)
      .toLowerCase()
      .replace(/["']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function findToggleBtn(cmp, attr, wanted) {
    if (!wanted) return null;
    var want = slug(wanted);
    if (!want) return null;
    var btns = Array.prototype.slice.call(
      cmp.querySelectorAll('.ampd-compare__toggle-btn[' + attr + ']')
    );
    var exact = null;
    var partial = null;
    btns.forEach(function (b) {
      if (b.classList.contains('is-disabled')) return;
      var val = slug(b.getAttribute(attr));
      var label = slug(b.textContent);
      if (!exact && (val === want || label === want)) exact = b;
      // "16" against "16-mini-models": only as a fallback, so an exact match
      // always wins over a substring one.
      if (!partial && label.indexOf(want) !== -1) partial = b;
    });
    return exact || partial;
  }

  function applyDeepLink(cmp) {
    if (!window.URLSearchParams) return;
    var params = new URLSearchParams(window.location.search);
    var size = params.get('size') || params.get('model');
    var series = params.get('series');
    var frame = params.get('frame');
    if (!size && !series && !frame) return;

    // Series first: it decides which sizes exist, so setting it after a size
    // could immediately invalidate that size.
    var seriesBtn = findToggleBtn(cmp, 'data-ampd-series', series);
    if (seriesBtn) {
      setActive(seriesBtn.closest('.ampd-compare__toggle'), seriesBtn);
      cmp.dataset.ampdSeries = seriesBtn.getAttribute('data-ampd-series');
      syncSizeAvailability(cmp);
      applyPanels(cmp);
      syncFrameAvailability(cmp);
    }

    var sizeBtn = findToggleBtn(cmp, 'data-ampd-set', size);
    if (sizeBtn) {
      setActive(sizeBtn.closest('.ampd-compare__toggle'), sizeBtn);
      cmp.dataset.ampdSize = sizeBtn.getAttribute('data-ampd-set');
      applyPanels(cmp);
      syncFrameAvailability(cmp);
    }

    var frameBtn = findToggleBtn(cmp, 'data-ampd-frame', frame);
    if (frameBtn) {
      setActive(frameBtn.closest('.ampd-compare__toggle'), frameBtn);
      showFrame(cmp, frameBtn.getAttribute('data-ampd-frame'));
    }
  }

  /* ---------- colour swatches ---------- */
  // Carry the chosen colour through to the product page, so the customer lands
  // on the variant they were looking at rather than the product's default.
  function variantUrl(base, vid) {
    if (!base || !vid) return base;
    return base + (base.indexOf('?') === -1 ? '?' : '&') + 'variant=' + vid;
  }

  // The Buy Now button sits outside the frame groups, so it follows whichever
  // frame is currently on screen rather than any one group's swatches.
  function syncCellLinks(cell) {
    if (!cell) return;
    var base = cell.getAttribute('data-ampd-url');
    if (!base) return;
    var frames = cell.querySelectorAll('.ampd-compare__frame');
    var visible = null;
    frames.forEach(function (f) { if (!visible && !f.hidden) visible = f; });
    if (!visible) visible = frames[0];
    var active = visible ? visible.querySelector('.ampd-compare__swatch.is-active') : null;
    var vid = active && active.getAttribute('data-ampd-variant');
    var url = variantUrl(base, vid);
    cell.querySelectorAll('[data-ampd-buy], [data-ampd-soldout-btn]').forEach(function (btn) {
      btn.setAttribute('href', url);
    });
    syncSoldOut(cell, visible, active);
  }

  /* ---------- sold-out state ----------
     What the customer is looking at is a frame + colour, so availability has to
     be read from the selected swatch rather than the product: an Ace can be in
     stock in Step Over and sold out in Step Through. Products with no colours
     fall back to the frame panel's own flag. */
  function syncSoldOut(cell, frame, active) {
    var soldOut;
    if (active) soldOut = active.getAttribute('data-ampd-avail') === '0';
    else if (frame) soldOut = frame.getAttribute('data-ampd-avail') === '0';
    else return;

    cell.classList.toggle('is-soldout', soldOut);

    var badge = cell.querySelector('[data-ampd-oos]');
    if (badge) badge.hidden = !soldOut;

    // Same button in both states - only the wording changes, so a sold-out
    // card doesn't read as a different kind of card.
    var buy = cell.querySelector('[data-ampd-buy]');
    var soldOutBtn = cell.querySelector('[data-ampd-soldout-btn]');
    if (buy) buy.hidden = soldOut;
    if (soldOutBtn) soldOutBtn.hidden = !soldOut;
  }

  function setSwatch(sw) {
    var frame = sw.closest('.ampd-compare__frame');
    if (!frame) return;
    var img = frame.querySelector('.ampd-compare__img');
    if (img && sw.dataset.ampdImg) {
      img.setAttribute('src', sw.dataset.ampdImg);
      img.removeAttribute('srcset');
    }
    frame.querySelectorAll('.ampd-compare__swatch').forEach(function (s) {
      s.classList.toggle('is-active', s === sw);
    });
    var cell = frame.closest('.ampd-compare__cell--head');
    var link = frame.querySelector('.ampd-compare__media-link');
    if (link && cell) {
      link.setAttribute('href', variantUrl(cell.getAttribute('data-ampd-url'), sw.getAttribute('data-ampd-variant')));
    }
    syncCellLinks(cell);
  }

  function initSwatches(cmp) {
    cmp.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var sw = e.target.closest('.ampd-compare__swatch');
      if (sw) {
        setSwatch(sw);
        return;
      }
    });
    // Default each frame group to its first swatch.
    cmp.querySelectorAll('.ampd-compare__frame').forEach(function (frame) {
      var first = frame.querySelector('.ampd-compare__swatch');
      if (first) setSwatch(first);
    });
    // Products with no colour options have no swatch to trigger the above.
    cmp.querySelectorAll('.ampd-compare__cell--head').forEach(syncCellLinks);
  }

  /* ---------- sticky header measurement ---------- */
  function measure(cmp) {
    // Only visible panels have a real height; hidden ones measure 0.
    var medias = cmp.querySelectorAll('.ampd-compare__media');
    var h = 0;
    medias.forEach(function (m) {
      if (m.offsetParent === null) return;
      var r = m.getBoundingClientRect().height;
      if (r > h) h = r;
    });
    if (!h) return;
    var cs = window.getComputedStyle(medias[0]);
    h += parseFloat(cs.marginBottom) || 0;
    cmp.style.setProperty('--ampd-head-pull', Math.round(h) + 'px');
  }

  function initMeasure(cmp) {
    measure(cmp);
    window.addEventListener('load', function () { measure(cmp); });
    cmp.querySelectorAll('.ampd-compare__img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', function () { measure(cmp); }, { once: true });
    });
    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () {
        measure(cmp);
        cmp.querySelectorAll('.ampd-compare__toggle').forEach(moveThumb);
      }, 150);
    });
  }

  /* ---------- boot ---------- */
  function init() {
    document.querySelectorAll('ampd-compare').forEach(function (cmp) {
      if (cmp.dataset.ampdInit) return;
      cmp.dataset.ampdInit = '1';
      initSync(cmp);
      initToggles(cmp);
      initSwatches(cmp);
      initMeasure(cmp);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', init);
})();
